import { useCallback } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { Controller, useForm } from 'react-hook-form';

import { Unit, convertDisplayValueToNormalizedDbValues, getDisplayUnitAndSymbol, isFiniteNumber } from '@core';
import { ParameterNode } from '@data';
import { useGoalParameterSetStepper } from '@hooks';
import { useTranslate } from '@i18n';
import { useDeleteCustomCorridor, useUpsertCustomCorridor } from '@queries';
import {
    activityAtom,
    activityUnitAtom,
    convertedLayoutCustomCorridorsMapAtom,
    corridorsAtom,
    goalCorridorParameterAtom,
    setGoalCorridorParameterAtom,
} from '@store';
import { Button, GoalParameterSet, ModalBase, ModalBody, ModalFooter, ModalHeader } from '@ui';

import { container } from './GoalSetterModal.css';

type GoalCorridorFormData = { goal: { min: number; max: number } };

export type GoalSetterModalProps = {
    parameter: ParameterNode;
};

export const GoalSetterModal = ({ parameter }: GoalSetterModalProps) => {
    const setGoalCorridorParameter = useSetAtom(setGoalCorridorParameterAtom);
    const t = useTranslate();
    const activityUnit = useAtomValue(activityUnitAtom);
    const activity = useAtomValue(activityAtom);
    const corridors = useAtomValue(corridorsAtom);
    const layoutCorridorMap = useAtomValue(convertedLayoutCustomCorridorsMapAtom);
    const { mutateAsync: deleteGoal } = useDeleteCustomCorridor();
    const parameterName = parameter.parameter.name;
    const parameterId = parameter.parameter.smpId;
    const unit = parameter.parameter.unit as Unit;
    const onDismiss = useCallback(() => setGoalCorridorParameter(null), [setGoalCorridorParameter]);
    const { symbol: displaySymbol, unit: displayUnit } = getDisplayUnitAndSymbol(unit, activityUnit, true);
    const { mutateAsync: upsert } = useUpsertCustomCorridor();

    const layoutCorridor = layoutCorridorMap?.[parameterId];
    const corridorMargins = corridors?.[parameterName];

    const methods = useForm<GoalCorridorFormData>({
        defaultValues: {
            goal: {
                max: layoutCorridor?.innerMax ?? corridorMargins?.innerMax,
                min: layoutCorridor?.innerMin ?? corridorMargins?.innerMin,
            },
        },
        mode: 'onChange',
    });

    const {
        handleSubmit,
        control,
        formState: { isValid },
    } = methods;

    const handleSave = useCallback(
        (form: GoalCorridorFormData) => {
            if (!activity?.layoutId) return;
            const { min, max } = form.goal;
            const innerMin = convertDisplayValueToNormalizedDbValues({ value: min, displayUnit });
            const innerMax = convertDisplayValueToNormalizedDbValues({ value: max, displayUnit });

            upsert({
                swingMeasuredParameterId: parameterId,
                innerMin,
                innerMax,
                layoutId: activity.layoutId,
            });
            onDismiss();
        },
        [activity?.layoutId, displayUnit, upsert, parameterId, onDismiss],
    );

    const handleDelete = useCallback(() => {
        if (!layoutCorridor?.id) {
            return;
        }
        deleteGoal(layoutCorridor?.id);
        onDismiss();
    }, [deleteGoal, layoutCorridor?.id, onDismiss]);

    const stepper = useGoalParameterSetStepper({ displayUnit, corridorMargins });

    return (
        <ModalBase onDismiss={onDismiss} open variant="partial">
            <ModalHeader>Add custom corridor to {'current goal'}</ModalHeader>
            <ModalBody>
                <div className={container}>
                    <Controller
                        name="goal"
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (!isFiniteNumber(value?.min) || !isFiniteNumber(value?.max)) {
                                    return false;
                                }
                                if (value.min >= value.max) {
                                    return false;
                                }
                                return true;
                            },
                        }}
                        render={({ field: { value, onChange } }) => (
                            <GoalParameterSet
                                onDelete={
                                    (layoutCorridor && typeof layoutCorridor.id) === 'number' ? handleDelete : undefined
                                }
                                corridor={corridorMargins}
                                title={t(parameter.i18n ?? '-')}
                                displaySymbol={displaySymbol}
                                onUpdate={onChange}
                                min={value.min}
                                max={value.max}
                                stepper={stepper}
                            />
                        )}
                    />
                </div>
            </ModalBody>
            <ModalFooter
                primary={
                    <Button isDisabled={!isValid} onClick={handleSubmit(handleSave)}>
                        Save
                    </Button>
                }
            />
        </ModalBase>
    );
};

export const GoalSetterModalLauncher = () => {
    const activity = useAtomValue(activityAtom);
    const parameter = useAtomValue(goalCorridorParameterAtom);

    if (!parameter || !activity?.layoutId) return null;

    return <GoalSetterModal parameter={parameter} />;
};
